import React, { ReactNode } from "react";

import { decode } from "he";
import isValidAbsoluteUrl from "validator/lib/isURL";

export type AnchorProps = {
    readonly children?: ReactNode;
    readonly testId?: string;
} & React.AnchorHTMLAttributes<HTMLAnchorElement>;

const decodeChildren = (children: ReactNode): ReactNode => {
    if (typeof children === "string") {
        return decode(children);
    }
    return children;
};

export const Anchor = ({ children, testId, ...anchorProps }: AnchorProps) => {
    const { href } = anchorProps;
    const TRUSTED_HOSTS = ["id.atlassian.com", "id.stg.internal.atlassian.com"];
    const decodedChildren = decodeChildren(children);

    if (!href) {
        // Remove empty string `href` from render if present
        const { href: emptyHref, ...rest } = anchorProps;
        return (
            <span {...rest} data-testid={testId}>
                {decodedChildren}
            </span>
        );
    } else if (isValidAbsoluteUrl(href)) {
        const { host } = new URL(href);
        const isTrustedUrl = TRUSTED_HOSTS.includes(host);

        return (
            <a
                {...anchorProps}
                target={isTrustedUrl ? undefined : "_blank"}
                rel={isTrustedUrl ? undefined : "noopener"}
                data-testid={testId}
            >
                {decodedChildren}
            </a>
        );
    } else if (href.startsWith("#") || href.startsWith("mailto:")) {
        return (
            <a {...anchorProps} data-testid={testId}>
                {decodedChildren}
            </a>
        );
    } else {
        return (
            <a {...anchorProps} data-testid={testId} href={href}>
                {decodedChildren}
            </a>
        );
    }
};
